import { Loader2, X } from 'lucide-react'
import { FC, PropsWithChildren } from 'react'
import { Avatar, AvatarImage, AvatarFallback } from './avatar'
import { cn } from '@ui/lib/utils'

export const ChipsWrapper: FC<PropsWithChildren & { className?: string }> = ({
  children,
  className,
}) => {
  return (
    <div className={cn('flex flex-wrap gap-xxxs', className)}>{children}</div>
  )
}

type ChipsItemProps = {
  text: string
  icon?: JSX.Element
  disabled?: boolean
  isLoading?: boolean
  avatarSrc?: string
  onClick?: () => void
  onRemove?: () => void
  className?: string
}

export const ChipsItem: FC<ChipsItemProps> = ({
  text,
  icon,
  disabled,
  isLoading,
  avatarSrc,
  onClick,
  onRemove,
  className,
}) => {
  const handleRemove = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation()
    e.preventDefault()
    if (disabled) return
    onRemove?.()
  }

  return (
    <div
      onClick={disabled ? undefined : onClick}
      onPointerDown={(e) => {
        e.stopPropagation()
        e.preventDefault()
      }}
      className={cn(
        'flex cursor-pointer items-center gap-xxxs rounded-sm bg-gray-100 p-xxs text-sm font-medium text-gray-900 transition-all hover:bg-gray-200 [&_svg]:size-4',
        disabled && 'cursor-not-allowed bg-gray-100 text-gray-300',
        className,
      )}
    >
      {avatarSrc && (
        <Avatar className={cn('size-6 text-[10px]')}>
          <AvatarImage src={avatarSrc} alt={text} />
          <AvatarFallback className="text-xs">
            {text.charAt(0).toUpperCase()}
          </AvatarFallback>
        </Avatar>
      )}
      {icon}
      <span>{text}</span>
      {onRemove && !isLoading && (
        // used <span/> instead of <button/> to allow ChipsItem to be used as part of another button without errors
        <span
          role="button"
          tabIndex={-1}
          onClick={handleRemove}
          className={cn(
            'pointer-events-auto cursor-pointer',
            disabled && 'pointer-events-none cursor-not-allowed opacity-50',
          )}
        >
          <X size={16} />
        </span>
      )}
      {isLoading && <Loader2 size={16} className="animate-spin" />}
    </div>
  )
}
